import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { persistBrowseName, pushEvent } from '../../utils/dataLayer';

// Deeplink: /dl/accessories?model=<id>&year=<id>&language=en|ar&show_3d=1&category_id=<id>
// Optional labels: model_name, year_name (recommended so GA gets readable model without waiting for UI)
// Attribution: add utm_source / utm_medium=deeplink / utm_campaign so GA session channel is not Direct
// Example Bronco: /dl/accessories?model=29&year=1&language=en&utm_medium=deeplink&utm_source=ford_com&model_name=Bronco
function AccessoriesDeepLink() {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            const searchParams = new URLSearchParams(location.search);
            const model = searchParams.get('model') || searchParams.get('model_id');
            const year = searchParams.get('year') || searchParams.get('year_id') || 'all';
            let modelName = searchParams.get('model_name');
            let yearName = searchParams.get('year_name');
            const language = searchParams.get('language') || 'en';
            const show3d = searchParams.get('show_3d') || searchParams.get('show3d');
            const show3dOnly =
                process.env.REACT_APP_BRAND_ID === '1' && (show3d === '1' || show3d === 'true');
            const categoryId = searchParams.get('category_id') || searchParams.get('category');

            if (language) localStorage.setItem('language', language);
            if (model) localStorage.setItem('selectedModel', model);
            localStorage.setItem('selectedYear', year || 'all');
            if (modelName) persistBrowseName('selectedModelName', modelName);
            if (yearName) persistBrowseName('selectedYearName', yearName);
            if (categoryId) localStorage.setItem('selectedCategory', categoryId);
            if (show3dOnly) {
                localStorage.setItem('show3dOnly', '1');
            } else {
                localStorage.removeItem('show3dOnly');
            }

            // Resolve readable model/year when partners omit labels (IDs alone are useless in Explore)
            if (model && (!modelName || (!yearName && year && year !== 'all'))) {
                try {
                    const dealerId = localStorage.getItem('dealer_id') || '';
                    const token = 'Bearer ' + process.env.REACT_APP_FORD_API_TOKEN;
                    const config = {
                        headers: {
                            Authorization: token,
                            'X-Locale': language,
                        },
                    };
                    const res = await axios.get(
                        `${process.env.REACT_APP_FORD_API_URL}/api/v1/car-models/${model}?dealer_id=${dealerId}&brand_id=${process.env.REACT_APP_BRAND_ID}&market_id=${process.env.REACT_APP_MARKET_ID}`,
                        config
                    );
                    if (!cancelled && res.data?.name && !modelName) {
                        modelName = res.data.name;
                        persistBrowseName('selectedModelName', modelName);
                    }
                    if (!cancelled && !yearName && year && year !== 'all' && Array.isArray(res.data?.years)) {
                        const match = res.data.years.find((y) => String(y.id) === String(year));
                        if (match?.year || match?.name) {
                            yearName = String(match.year || match.name);
                            persistBrowseName('selectedYearName', yearName);
                        }
                    }
                } catch (_) {
                    /* ignore — still navigate with IDs */
                }
            }

            if (cancelled) return;

            pushEvent('site_deeplink_opened', {
                model_id: model || undefined,
                model_name: modelName || undefined,
                year_id: year || undefined,
                year_name: yearName || undefined,
                show_3d_only: show3dOnly ? 1 : 0,
                category_id: categoryId || undefined,
                utm_source: searchParams.get('utm_source') || undefined,
                utm_medium: searchParams.get('utm_medium') || undefined,
                utm_campaign: searchParams.get('utm_campaign') || undefined,
            });

            navigate('/accessory', {
                state: {
                    selectedModel: model || undefined,
                    selectedYear: year || undefined,
                    show3dOnly: show3dOnly || undefined,
                    categoryId: categoryId || undefined,
                },
            });
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [navigate, location]);

    return (
        <div className="loading">
            <p>Loading...</p>
        </div>
    );
}

export default AccessoriesDeepLink;
